/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import type { $Dealer } from 'CoreTypes';
import type { $StoreState } from '../../types/StoreState';
import type { $WidgetSettings } from '../../types/WidgetSettings';
import type { $WidgetEnvConfiguration } from '../../types/WidgetEnvConfiguration';
import { TrackingAttributes } from 'CoreConstants/Tracking';
import WorkbenchIcon from 'CoreComponents/WorkbenchIcon/WorkbenchIcon';
import WorkbenchIcons from 'CoreComponents/WorkbenchIcon/WorkbenchIcons';
import { isFunction } from 'CoreUtils/WidgetUtils';
import ProgressLoader from 'CoreComponents/ProgressLoader/ProgressLoader';
import DealerData from '../DealerData/DealerData';
import { unsetPreviewedDealer } from '../../store/dealer';
import { getEnvConfiguration } from '../../environments';
import { getWidgetFieldValueByKey } from 'CoreUtils/WidgetFieldUtils';
import Keys from '../../constants/WidgetFieldsKeys';
import DMSConsumer from 'CoreConsumers/DMSConsumer';
import { getDMSEndpointURL } from 'CoreUtils/EndpointUtils';

type $Props = {
  previewedDealer: {
    outletId: string,
    position: string
  },
  selectDealerCallback?: Function,
  unsetPreviewedDealer: Function,
  settings: $WidgetSettings,
  localeLanguage: boolean,
};

type $State = {
  isModalOpen: boolean,
  selectedDealer: ?$Dealer,
  loadingDealer: boolean
};

class SearchResultsMapModal extends Component<$Props, $State> {
  state = {
    isModalOpen: false,
    selectedDealer: null,
    loadingDealer: false
  };

  componentWillUnmount() {
    this.props.unsetPreviewedDealer(true);
  }

  UNSAFE_componentWillReceiveProps(nextProps: $Props): void { // eslint-disable-line
    const {
      outletId = ''
    } = this.props.previewedDealer;
    const {
      outletId: nextOutletId = ''
    } = nextProps.previewedDealer;

    if(nextOutletId !== "" && outletId !== nextOutletId)
      this.handleDealerClick(nextOutletId);
  }

  handleDealerClick = async (outletId: string) => {
    this.setState({
      isModalOpen: true,
      loadingDealer: true
    });

    const envConfig: $WidgetEnvConfiguration = getEnvConfiguration(this.props.settings.env);
    const dms = new DMSConsumer(getDMSEndpointURL(this.props.settings, envConfig));

    try {
      const dealer = await dms.getDealerDataByExternalId(
        outletId,
        this.props.settings.market || '',
        this.props.localeLanguage
      );

      this.setState({
        loadingDealer: false,
        selectedDealer: dealer
      });
    }
    catch (error) {
      this.setState({ loadingDealer: false });
    }
  };

  handleCloseModal = () => {
    this.props.unsetPreviewedDealer();
    this.setState({
      isModalOpen: false,
      selectedDealer: null
    });
  };

  handleSelectDealerClick = (dealer: Object) => {
    this.props.unsetPreviewedDealer();
    const { selectDealerCallback } = this.props;
    if (selectDealerCallback && isFunction(selectDealerCallback)) {
      selectDealerCallback(dealer);
    }
    this.setState({
      isModalOpen: false,
      selectedDealer: null
    });
  };

  handleModalClick = (event: SyntheticInputEvent<HTMLInputElement>) => event.stopPropagation();

  render() {

    return this.state.isModalOpen && (
      <div className="dsw-search-results-map-modal__overlay" onClick={this.handleCloseModal}>
        <article className="dsw-search-results-map-modal__content" onClick={this.handleModalClick}>
          <div className="dsw-search-results-map-modal__header">
            <button type="button" onClick={this.handleCloseModal}>
              <WorkbenchIcon icon={WorkbenchIcons.ICO_CLOSE} />
            </button>
          </div>
          <div className="dsw-search-results-map-modal__body">
            {this.state.loadingDealer && (
              <div className="dsw-search-results-map-modal__body-loader">
                <div className="dsw-search-results-map-modal__body-loader--wrapper">
                  <ProgressLoader loading />
                </div>
              </div>
            )}
            {this.state.selectedDealer && (
              <DealerData
                dealer={this.state.selectedDealer}
                selectDealerCallback={this.handleSelectDealerClick}
                trackingLinkPosition={TrackingAttributes.LINK_POSITION_DSW_DEALER_MAP}
              />
            )}
            {/* TODO: A case for when the selected dealer request fails */}
          </div>
        </article>
      </div>
    );
  }
}

const mapStateToProps = (state: $StoreState) => {
  const languages = state.dsw.configuration? state.dsw.configuration.languages : [];
  return {
    settings: state.dsw.settings,
    configuration: getEnvConfiguration(state.dsw.settings.env).mapProviders,
    hereMapsLanguage: getWidgetFieldValueByKey(Keys.HERE_MAPS_LANGUAGE, state.dsw),
    selectDealerCallback: state.dsw.settings.selectDealerCallback,
    searchTerm: state.dsw.search.searchTerm,
    localeLanguage: !!(languages && languages[0] && languages[0].localeLanguage),
    // $FlowFixMe
    previewedDealer: state.dsw.dealer.previewedDealer
  };
};

const mapDispatchToProps: Object = {
  unsetPreviewedDealer
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchResultsMapModal);
